<template>
  <TooltipProvider>
    <div class="min-h-screen bg-zinc-900 font-poppins text-zinc-100">
      <!-- Left Sidebar (Desktop) -->
      <aside
        :class="[
          'fixed left-0 top-0 h-full border-r border-white/5 backdrop-blur-md py-4 pr-3 pl-3 flex flex-col transition-all duration-300 custom-scrollbar z-30 lg:block overflow-y-auto',
          isOpen ? 'w-64' : 'w-20',
          isMobile ? 'hidden' : '',
          'bg-zinc-900/80'
        ]"
      >
        <!-- Logo & Toggle -->
        <div class="mb-6 flex items-center" :class="isOpen ? 'justify-between px-2' : 'justify-center'">
          <img
            v-if="isOpen"
            src="@/assets/bot_express_logo.png"
            alt="Logo"
            class="h-auto w-32 drop-shadow-glow"
          />
          <img
            v-else
            src="/favicon.ico"
            alt="Logo"
            class="h-10 w-10 drop-shadow-glow"
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                @click="toggleSidebar" 
                class="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 p-1.5 rounded-lg transition-all duration-200"
              >
                <StepForward v-if="!isOpen" class="h-4 w-4" />
                <StepBack v-else class="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{{ isOpen ? 'Minimizar' : 'Expandir' }}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <!-- User Progress Card -->
        <UserProgressCard v-if="isOpen" class="mb-4" />

        <!-- Navigation -->
        <nav class="flex-1 space-y-1">
          <!-- Main Items -->
          <template v-for="item in allMenuItems" :key="item.name">
            <div v-if="!item.hide && !item.section">
              <Tooltip :delay-duration="300" :disabled="isOpen">
                <TooltipTrigger asChild>
                  <component
                    :is="item.external ? 'a' : 'RouterLink'"
                    v-bind="item.external ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' } : { to: { name: item.href } }"
                    :class="[
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-500/5 text-white border-l-2 border-emerald-500'
                        : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white border-l-2 border-transparent',
                      'group flex items-center rounded-r-lg px-3 py-2 text-sm font-medium transition-all duration-200 relative',
                      isOpen ? '' : 'justify-center'
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        isActive(item.href) ? 'text-emerald-400' : 'text-zinc-400 group-hover:text-white',
                        'h-5 w-5 flex-shrink-0 transition-colors',
                        isOpen ? 'mr-3' : ''
                      ]"
                    />
                    <span v-if="isOpen" :class="{ 'text-white font-medium': isActive(item.href) }">{{ item.name }}</span>
                    <!-- <div 
                      v-if="item.new && isOpen"
                      class="ml-auto bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    >
                      NOVO
                    </div> -->
                  </component>
                </TooltipTrigger>
                <TooltipContent side="right" v-if="!isOpen">
                  <p>{{ item.name }}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            <!-- Section Divider -->
            <div v-else-if="item.section && isOpen" class="pt-4 pb-2 px-3">
              <div class="flex items-center gap-2">
                <div class="h-px flex-1 bg-gradient-to-r from-transparent via-zinc-700/50 to-transparent"></div>
                <span class="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{{ item.section }}</span>
                <div class="h-px flex-1 bg-gradient-to-r from-transparent via-zinc-700/50 to-transparent"></div>
              </div>
            </div>
            <div v-else-if="item.section && !isOpen" class="py-2">
              <div class="h-px bg-zinc-700/50 mx-auto w-8"></div>
            </div>
          </template>

          <!-- Bottom Section -->
          <div class="pt-4 mt-auto border-t border-zinc-700/30">
            <template v-for="item in bottomMenuItems" :key="item.name">
              <Tooltip :delay-duration="300" :disabled="isOpen">
                <TooltipTrigger asChild>
                  <component
                    :is="item.external ? 'a' : 'RouterLink'"
                    v-bind="item.external ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' } : { to: { name: item.href } }"
                    :class="[
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-500/5 text-white'
                        : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white',
                      'group flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
                      isOpen ? '' : 'justify-center'
                    ]"
                  >
                    <component
                      :is="item.icon"
                      :class="[
                        isActive(item.href) ? 'text-emerald-400' : 'text-zinc-400 group-hover:text-white',
                        'h-5 w-5 flex-shrink-0 transition-colors',
                        isOpen ? 'mr-3' : ''
                      ]"
                    />
                    <span v-if="isOpen">{{ item.name }}</span>
                  </component>
                </TooltipTrigger>
                <TooltipContent side="right" v-if="!isOpen">
                  <p>{{ item.name }}</p>
                </TooltipContent>
              </Tooltip>
            </template>
            
            <!-- Logout Button -->
            <Tooltip :delay-duration="300" :disabled="isOpen">
              <TooltipTrigger asChild>
                <button
                  @click="handleLogout"
                  :class="[
                    'w-full group flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 text-zinc-400 hover:bg-red-500/10 hover:text-red-400',
                    isOpen ? '' : 'justify-center'
                  ]"
                >
                  <LogOut
                    :class="[
                      'h-5 w-5 flex-shrink-0 transition-colors text-zinc-400 group-hover:text-red-400',
                      isOpen ? 'mr-3' : ''
                    ]"
                  />
                  <span v-if="isOpen">Sair</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" v-if="!isOpen">
                <p>Sair</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <!-- Social Media -->
          <div v-if="isOpen" class="mt-3 bg-zinc-800/50 rounded-lg p-2">
            <div class="grid grid-cols-3 gap-1.5">
              <a 
                href="https://instagram.com/expressoficial_bot" 
                target="_blank" 
                rel="noopener noreferrer"
                class="flex items-center justify-center p-2 rounded-md bg-gradient-to-br from-purple-600/10 to-pink-500/10 hover:from-purple-600/20 hover:to-pink-500/20 transition-all duration-200"
              >
                <Instagram class="h-4 w-4 text-pink-400" />
              </a>
              <a 
                href="https://wa.me/5511918600431" 
                target="_blank" 
                rel="noopener noreferrer"
                class="flex items-center justify-center p-2 rounded-md bg-gradient-to-br from-[#25D366]/10 to-[#128C7E]/10 hover:from-[#25D366]/20 hover:to-[#128C7E]/20 transition-all duration-200"
              >
                <WhatsappIcon class="h-4 w-4 fill-emerald-400" />
              </a>
              <a 
                href="https://t.me/botexpressoficial" 
                target="_blank" 
                rel="noopener noreferrer"
                class="flex items-center justify-center p-2 rounded-md bg-gradient-to-br from-blue-500/10 to-cyan-400/10 hover:from-blue-500/20 hover:to-cyan-400/20 transition-all duration-200"
              >
                <Send class="h-4 w-4 text-blue-400" />
              </a>
            </div>
          </div>

          <!-- Social Media Collapsed -->
          <div v-if="!isOpen" class="mt-3 flex flex-col items-center space-y-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <a 
                  href="https://instagram.com/expressoficial_bot" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-purple-600/10 to-pink-500/10 hover:from-purple-600/20 hover:to-pink-500/20 transition-all duration-200"
                >
                  <Instagram class="h-4 w-4 text-pink-400" />
                </a>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Instagram</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <a 
                  href="https://wa.me/5511918600431" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-[#25D366]/10 to-[#128C7E]/10 hover:from-[#25D366]/20 hover:to-[#128C7E]/20 transition-all duration-200"
                >
                  <WhatsappIcon class="h-4 w-4 fill-emerald-400" />
                </a>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>WhatsApp</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <a 
                  href="https://t.me/botexpressoficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500/10 to-cyan-400/10 hover:from-blue-500/20 hover:to-cyan-400/20 transition-all duration-200"
                >
                  <Send class="h-4 w-4 text-blue-400" />
                </a>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Telegram</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </nav>
      </aside>

      <!-- Mobile Bottom Navigation -->
      <div 
        class="mobile-bottom-navigation fixed bottom-0 left-0 right-0 bg-zinc-900/95 backdrop-blur-lg border-t border-white/5 z-30 lg:hidden"
        style="box-shadow: 0 -10px 20px rgba(0, 0, 0, 0.3);"
      >
        <div class="flex justify-between items-center px-2 h-16">
          <!-- Main Navigation Items -->
          <template v-for="(item, index) in mobileNavItems" :key="index">
            <component
              :is="item.external ? 'a' : 'RouterLink'"
              v-bind="item.external ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' } : { to: { name: item.href } }"
              class="flex flex-col items-center justify-center px-2 py-1 rounded-lg transition-all duration-200 relative"
              :class="[
                isActive(item.href) 
                  ? 'text-white' 
                  : 'text-zinc-400 hover:text-white'
              ]"
            >
              <div 
                v-if="isActive(item.href)" 
                class="absolute -top-3 w-10 h-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-glow-sm"
              ></div>
              <component
                :is="item.icon"
                class="h-6 w-6 mb-1"
                :class="[
                  isActive(item.href) 
                    ? 'text-emerald-400' 
                    : 'text-zinc-400'
                ]"
              />
              <span class="text-xs font-medium">{{ item.name }}</span>
            </component>
          </template>
          
          <!-- More Menu Button -->
          <button 
            @click="() => mobileMenuOpen = !mobileMenuOpen"
            class="flex flex-col items-center justify-center px-2 py-1 rounded-lg transition-all duration-200 relative"
            :class="[
              mobileMenuOpen 
                ? 'text-white' 
                : 'text-zinc-400 hover:text-white'
            ]"
          >
            <div 
              v-if="mobileMenuOpen" 
              class="absolute -top-3 w-10 h-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-glow-sm"
            ></div>
            <CogIcon class="h-6 w-6 mb-1" :class="{ 'text-emerald-400': mobileMenuOpen, 'text-zinc-400': !mobileMenuOpen }" />
            <span class="text-xs font-medium">Mais</span>
          </button>
        </div>
      </div>
      
      <!-- Slide-up Mobile Menu -->
      <Transition name="slide-up">
        <div 
          v-if="mobileMenuOpen" 
          class="fixed inset-x-0 bottom-0 z-50 lg:hidden"
          style="box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.5); max-height: 85dvh;"
        >
          <div class="bg-zinc-800 rounded-t-3xl overflow-hidden border-t border-l border-r border-emerald-500/10 h-full flex flex-col" style="max-height: 80dvh">
            <!-- Menu Handle Bar -->
            <div class="flex justify-center pt-3 pb-1">
              <div class="w-12 h-1.5 bg-zinc-600 rounded-full"></div>
            </div>
            
            <!-- Cabeçalho do menu -->
            <div class="flex justify-between items-center px-6 py-4 border-b border-zinc-700/50">
              <h2 class="text-xl font-bold text-white">Menu</h2>
              <button 
                @click="() => mobileMenuOpen = false"
                class="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg"
                style="box-shadow: 0 0 15px rgba(16, 185, 129, 0.3);"
              >
                <X class="h-5 w-5" />
              </button>
            </div>
            
            <!-- Conteúdo rolável -->
            <div class="flex-1 overflow-y-auto custom-scrollbar" style="max-height: 100%">
              <div class="py-2">
                <template v-for="item in menuDropdowns[0].children" :key="item.name">
                  <component
                    :is="item.external ? 'a' : 'RouterLink'"
                    v-if="!item.hide"
                    :to="!item.external ? { name: item.href } : null"
                    :href="item.external ? item.href : null"
                    :target="item.external ? '_blank' : null"
                    :rel="item.external ? 'noopener noreferrer' : null"
                    class="flex items-center px-6 py-4 text-zinc-100 hover:bg-zinc-700/50 transition-colors duration-200"
                    :class="{ 'bg-gradient-to-r from-emerald-500/20 to-transparent': isActive(item.href) }"
                    @click="() => mobileMenuOpen = false"
                  >
                    <div 
                      :class="[
                        isActive(item.href) 
                          ? 'bg-gradient-to-br from-emerald-500 to-teal-600' 
                          : 'bg-zinc-700/70',
                        'flex items-center justify-center w-10 h-10 rounded-full mr-4'
                      ]"
                      :style="isActive(item.href) ? 'box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);' : ''"
                    >
                      <component
                        :is="item.icon"
                        :class="[
                          isActive(item.href) ? 'text-white' : 'text-zinc-300',
                          'h-5 w-5'
                        ]"
                      />
                    </div>
                    <div class="flex-1">
                      <span class="text-base font-medium" :class="{ 'text-emerald-400': isActive(item.href) }">{{ item.name }}</span>
                    </div>
                    <div class="flex items-center">
                      <div 
                        v-if="isActive(item.href)" 
                        class="w-2 h-2 rounded-full bg-emerald-500 mr-3"
                        style="box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);"
                      ></div>
                      <ChevronRight v-if="!item.external" class="h-5 w-5 text-zinc-500" />
                      <ExternalLink v-else class="h-4 w-4 text-zinc-500" />
                    </div>
                  </component>
                </template>
                
                <!-- Ferramentas dropdown items for mobile -->
                <template v-for="item in menuDropdowns[1].children" :key="item.name">
                  <component
                    :is="item.external ? 'a' : 'RouterLink'"
                    v-if="!item.hide"
                    :to="!item.external ? { name: item.href } : null"
                    :href="item.external ? item.href : null"
                    :target="item.external ? '_blank' : null"
                    :rel="item.external ? 'noopener noreferrer' : null"
                    class="flex items-center px-6 py-4 text-zinc-100 hover:bg-zinc-700/50 transition-colors duration-200"
                    :class="{ 'bg-gradient-to-r from-emerald-500/20 to-transparent': isActive(item.href) }"
                    @click="() => mobileMenuOpen = false"
                  >
                    <div 
                      :class="[
                        isActive(item.href) 
                          ? 'bg-gradient-to-br from-emerald-500 to-teal-600' 
                          : 'bg-zinc-700/70',
                        'flex items-center justify-center w-10 h-10 rounded-full mr-4'
                      ]"
                      :style="isActive(item.href) ? 'box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);' : ''"
                    >
                      <component
                        :is="item.icon"
                        :class="[
                          isActive(item.href) ? 'text-white' : 'text-zinc-300',
                          'h-5 w-5'
                        ]"
                      />
                    </div>
                    <div class="flex-1">
                      <span class="text-base font-medium" :class="{ 'text-emerald-400': isActive(item.href) }">{{ item.name }}</span>
                    </div>
                    <div class="flex items-center">
                      <div 
                        v-if="isActive(item.href)" 
                        class="w-2 h-2 rounded-full bg-emerald-500 mr-3"
                        style="box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);"
                      ></div>
                      <ChevronRight v-if="!item.external" class="h-5 w-5 text-zinc-500" />
                      <ExternalLink v-else class="h-4 w-4 text-zinc-500" />
                    </div>
                  </component>
                </template>
                <!-- Logout Mobile -->
                <button
                  @click="handleLogout"
                  class="w-full flex items-center px-6 py-4 text-zinc-100 hover:bg-zinc-700/50 transition-colors duration-200 mt-2 border-t border-zinc-700/50"
                >
                  <div class="bg-zinc-700/70 flex items-center justify-center w-10 h-10 rounded-full mr-4 group-hover:bg-red-500/20">
                    <LogOut class="h-5 w-5 text-zinc-300 group-hover:text-red-400" />
                  </div>
                  <div class="flex-1 text-left">
                    <span class="text-base font-medium">Sair</span>
                  </div>
                </button>
              </div>
            </div>
            
            <!-- Espaço para evitar que o conteúdo fique muito próximo do bottom navigation -->
            <div class="h-4"></div>
          </div>
        </div>
      </Transition>
      
      <!-- Backdrop for mobile menu -->
      <Transition name="fade">
        <div 
          v-if="mobileMenuOpen" 
          class="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden"
          @click="() => mobileMenuOpen = false"
        ></div>
      </Transition>
      
      <!-- Main Content -->
      <div 
        :class="[
          isMobile ? 'pl-0 pb-16' : (isOpen ? 'pl-64' : 'pl-20'), 
          'transition-all duration-300'
        ]"
      >
        <AdminLayoutNav />
        <div :class="[route.name === 'flows.show' ? '' : 'p-4 sm:p-6' ]">
          <router-view />
        </div>
      </div>
      <InstallPWADialog />
    </div>
    <Toaster />
    <SonnerToaster theme="dark" position="top-right" />
  </TooltipProvider>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import {
  ChartBarIcon,
  HomeIcon,
  CogIcon,
  UserIcon,
  CheckIcon
} from '@heroicons/vue/24/outline'
import { useRoute } from 'vue-router'
import {
  BotIcon,
  ChevronDown,
  ChevronRight,
  ClipboardIcon,
  Contact,
  CreditCardIcon,
  ExternalLink,
  Gift,
  GroupIcon,
  Headset,
  Instagram,
  KeyIcon,
  Link2,
  MessageCircle,
  MessageSquareMore,
  Package,
  Phone,
  Puzzle,
  RefreshCcw,
  Send,
  StepBack,
  StepForward,
  UserCog,
  Workflow,
  X,
  TrendingUp,
  LogOut,
  Wallet,
  UserCircle2
} from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Toaster } from "@/components/ui/toast"
import { Toaster as SonnerToaster } from "vue-sonner"
import "vue-sonner/style.css"
import AdminLayoutNav from './components/AdminLayoutNav.vue'
import { useBot } from '@/composables/useBot'
import InstallPWADialog from '@/components/ui/pwa/InstallPWADialog.vue'
import { usePaymentGatewayIntegration } from '@/composables/usePaymentGatewayIntegration'
import WhatsappIcon from '@/components/ui/icons/WhatsappIcon.vue'
import { useOrganization } from '@/composables/useOrganization'
import UserProgressCard from './components/UserProgressCard.vue'
import { useAuth } from '@/composables/useAuth'
import { usePushNotification } from '@/composables/usePushNotification'

const route = useRoute()

const { selectedBot } = useBot()
const { logout } = useAuth()
const { requestPermission, listenMessages } = usePushNotification()
const { isBetaTester } = useOrganization()
const { getOne: findOnePaymentGatewayIntegration, paymentGatewayIntegration } = usePaymentGatewayIntegration()
const isActive = (href: string) => route.name === href;

const isOpen = ref((localStorage.getItem('sidebarOpen') ?? 'true') === 'true')
const isMobile = ref(false)
const mobileMenuOpen = ref(false)

const handleLogout = () => {
    logout()
    window.location.href = '/login' // Force reload to clear state properly or use simple router push if available
}

const toggleSidebar = () => {
  isOpen.value = !isOpen.value
  localStorage.setItem('sidebarOpen', isOpen.value.toString())
}

const toggleDropdown = (dropdown: any) => {
  if (isOpen.value) {
    dropdown.open = !dropdown.open
  }
}

// Check if mobile on mount and window resize
const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024
}

interface MenuItem {
  name: string;
  href: string;
  icon: any;
  current: boolean;
  hide?: boolean;
  new?: boolean;
  external?: boolean;
  section?: string;
}

// Menu Items
const menuItems: MenuItem[] = [
  { name: 'Início', href: 'home', icon: HomeIcon, current: true, hide: false },
  { name: 'Vendas', href: 'reports.consult-orders', icon: ChartBarIcon, hide: false, current: false },
  { name: 'Relatórios', href: 'reports.dashboard', icon: TrendingUp, hide: false, current: false },
  { name: 'Conversas', href: 'chats', icon: MessageCircle, current: false, hide: !selectedBot.value },
  { name: 'Fluxo de Chat', href: 'flows.manage', icon: Workflow, current: false, hide: !selectedBot.value, new: true },
  { name: 'Palavras chaves', href: 'leads.start-parameters', icon: KeyIcon, current: false, hide: !selectedBot.value },
];

const bottomMenuItems: MenuItem[] = [
  { name: 'Meu Perfil', href: 'profile', icon: UserCircle2, current: false },
  { name: 'Ajuda', href: 'help', icon: Headset, current: false },
]

// Mobile navigation items (limited to 4 for bottom nav)
const mobileNavItems = computed(() => {
  // Start with main menu items
  const items = [...menuItems]
  
  // Add most important items from dropdown (customize as needed)
  items.push(
    { name: 'Bots', href: 'bots.manage', icon: BotIcon, current: false },
    { name: 'Planos', href: 'payment-plans.manage', icon: CreditCardIcon, current: false },
  )
  
  // Limit to 4 items for mobile nav
  return items.slice(0, 4) as MenuItem[]
})

const menuDropdowns = ref<{ icon: any; name: string; open: boolean; children: MenuItem[] }[]>([
  {
    icon: Puzzle,
    name: 'Integrações',
    open: false, 
    children: [
      { name: 'Integrações', href: 'integrations', icon: Puzzle, current: false, hide: !selectedBot.value },
      { name: 'Gateways de Pagamento', href: 'payment-gateway-integrations.manage', icon: Wallet, current: false, hide: !selectedBot.value },
      { name: 'Links Utmify', href: 'utm-links.manage', icon: Link2, current: false, hide: !selectedBot.value },
    ] 
  },
  {
    icon: CogIcon,
    name: 'Configurações',
    open: false, 
    children: [
      { name: 'Bots', href: 'bots.manage', icon: BotIcon, current: false },
      { name: 'Grupos e canais', href: 'groups.manage', icon: GroupIcon, current: false, hide: !selectedBot.value },
      { name: 'Planos', href: 'payment-plans.manage', icon: CreditCardIcon, current: false, hide: !selectedBot.value },
      { name: 'Order Bumps', href: 'order-bumps.manage', icon: TrendingUp, current: false, hide: !selectedBot.value, new: true },
      { name: 'Vídeo Chamadas', href: 'video-calls.manage', icon: Phone, current: false, hide: !selectedBot.value, new: true },
      { name: 'Links de Redirecionamento', href: 'links.manage', icon: ClipboardIcon, current: false, hide: !selectedBot.value },
      { name: 'Packs', href: 'packages.manage', icon: Package, current: false, hide: !selectedBot.value },
      { name: 'Disparo de mensagens', href: 'messages.manage', icon: Send, current: false, hide: !selectedBot.value },
      { name: 'Usuários', href: 'leads.manage', icon: Contact, current: false, hide: !selectedBot.value },
      { name: 'Splits', href: 'my-account', icon: UserCog, current: false, hide: !selectedBot.value },
      { name: 'Afiliação', href: 'affiliation.manage', icon: Gift, current: false, hide: !paymentGatewayIntegration.value?.allowReferral },
    ] 
  }
])

// Combine all menu items into a single flat array with sections
const allMenuItems = computed(() => {
  const items: MenuItem[] = [
    ...menuItems,
    // Section divider
    { section: 'Integrações', name: '', href: '', icon: null, current: false, hide: !selectedBot.value },
    ...menuDropdowns.value[0].children,
    // Section divider
    { section: 'Configurações', name: '', href: '', icon: null, current: false },
    ...menuDropdowns.value[1].children,
  ];
  return items.filter(item => !item.hide);
})

// Close mobile menu when clicking outside or changing route
const handleRouteChange = () => {
  mobileMenuOpen.value = false
}

onMounted(() => {
  // Check if mobile on mount
  checkMobile()
  
  // Add event listener for window resize
  window.addEventListener('resize', checkMobile)
  
  // Watch for route changes to close mobile menu
  if (route) {
    watch(() => route.path, handleRouteChange)
  }
})

onMounted(async () => {
  await findOnePaymentGatewayIntegration()
})

onMounted(async () => {
  await requestPermission()
  await listenMessages()
})

// Clean up event listener
onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})
</script>

<style>
/* Custom minimalist scrollbar */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
  overflow: overlay;
  scrollbar-gutter: stable;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px !important;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
}

/* Slide up animation for mobile menu */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* Fade animation for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Glow effects */
.drop-shadow-glow {
  filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.2));
}

.shadow-glow-sm {
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.4);
}

/* Social media card glow effects */
.from-purple-600\/10.to-pink-500\/10:hover {
  box-shadow: 0 0 15px rgba(219, 39, 119, 0.15);
}

.from-blue-500\/10.to-cyan-400\/10:hover {
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.15);
}

/* WhatsApp button glow effect */
.from-\[#25D366\]\/20.to-\[#128C7E\]\/20:hover {
  box-shadow: 0 0 15px rgba(37, 211, 102, 0.25);
}
</style>
